import React from "react";
import {isNullOrUndefined} from "../../Utilities/commonUtilities";
import {partialOptionIndexInArray} from "../../Utilities/Types/arrayUtilities";
import {InputLayout} from "./InputLayout";
import {appendedOtherValue, createFakeEvent, generateOtherValue, isValueInArray,} from "./inputUtility";
import {TextField} from "./TextField";
import {notificationSuffix, helpTextSuffix} from "./inputConstants";

import type {ListItem} from "../../Utilities/Types/listUtilities";
import type {ChangeHandler} from "../../types";

interface SelectFieldProps {
    defaultOption?: string;
    defaultOptionValue?: string;
    disabled?: boolean;
    error?: string;
    helpText?: string;
    hideLabel?: boolean;
    includeDefaultOption?: boolean;
    label: string;
    large_columns?: string;
    listHasTextField?: string[];
    medium_columns?: string;
    name: string;
    onChange?: ChangeHandler;
    options?: ListItem[];
    raiseHelpText?: boolean;
    responseFeedback?: string;
    value?: string | number;
    setValue?: (value: string) => void;
    setError?: (error: string) => void;
}

export const SelectField: React.FC<SelectFieldProps> = ({
								defaultOption,
								defaultOptionValue = "",
								disabled,
								error,
								helpText,
								hideLabel = false,
								includeDefaultOption = true,
								label,
								large_columns,
								listHasTextField = [],
								medium_columns,
								name,
								onChange,
								options,
								raiseHelpText,
								responseFeedback,
								value = "",
						}: SelectFieldProps) => {

	value = isNullOrUndefined(value) ? "" : value.toString();

	const handleUpdate = (selectedValue: string, otherValue: string) => {
		const value = isValueInArray(listHasTextField, selectedValue) ? generateOtherValue(selectedValue, otherValue) : selectedValue;
		onChange?.(createFakeEvent(value, `${name}`));
	};

	if (!defaultOption)
		defaultOption = "Select an Option";

	let selectListOptions: ListItem[] = [];
	if (options && options.length > 0) {
		selectListOptions = options;
	}

    const otherSelectListValueIndex = partialOptionIndexInArray(listHasTextField, value.toString());
	const hasTextField = otherSelectListValueIndex > -1;

	const selectListValue = hasTextField ? listHasTextField[otherSelectListValueIndex] : value;
	const otherTextValue = hasTextField ? appendedOtherValue(value, selectListValue) : "";

	return (
		<InputLayout
			disabled={disabled}
			helpText={helpText}
			hideLabel={hideLabel}
			raiseHelpText={raiseHelpText}
			label={label}
			large_columns={large_columns}
			medium_columns={medium_columns}
			name={name}
			error={error}
			responseFeedback={responseFeedback}
		>
			<select
				aria-disabled={disabled}
				aria-invalid={!!error}
				aria-errormessage={error ? `${name}${notificationSuffix}` : undefined}
				aria-describedby={helpText ? `${name}${helpTextSuffix}` : undefined}
				name={name}
				id={name}
				data-testid={name}
				value={selectListValue}
				onBlur={(event) => handleUpdate(event.target.value, otherTextValue)}
				onChange={onChange as React.ChangeEventHandler<HTMLSelectElement>}
				disabled={disabled}
			>
				{includeDefaultOption && <option value={defaultOptionValue}>{defaultOption}</option>}
				{selectListOptions.map((listItem: ListItem, index: number) => {
					return (
						<option key={`${name}${index}`} value={listItem.value}>
							{listItem.text}
						</option>);
				})
				}
			</select>
			{
				hasTextField  &&
				<TextField
					label={`Specify Other Text`}
					name={`${name}_text`}
					disabled={disabled}
				onChange={(event: React.ChangeEvent<HTMLInputElement>) => handleUpdate(selectListValue, event.target.value)}
					value={otherTextValue}
				/>
			}
		</InputLayout>
	);
};
